import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Popper,
  useMediaQuery,
} from "@mui/material";
import { Formik, useFormikContext } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import {
  executeAnyMutation,
  fetchJsonDynamic,
  fetchJsonDynamicFlatArgs,
} from "../DynamicServiceNew/jsondynamicservice";
import { generateQuery } from "./DynamicService";
import dayjs from "dayjs";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { NumericFormat } from "react-number-format";


const FormikObserver = ({
  onFieldChange,
  fields,
  setDependentOptions,
  setSubDependentOptions,
}) => {
  const { values } = useFormikContext();

  // ── Build a stable key from the fields that participate in a cascade ──────
  // ("main" or "dependent" fields). We re-run the preload effect whenever
  // this key changes, rather than only once on mount. This matters because
  // in edit mode the real ids (regionId, BankId, etc.) often arrive
  // asynchronously *after* the first render — Formik's `enableReinitialize`
  // then pushes them into `values`, but a mount-only effect would have
  // already run (with empty values) and never fire again, leaving the
  // dependent/subdependent option maps empty forever (permanent "Loading
  // options...").
  const cascadeFieldNames = fields
    .filter((f) => f.parent === "main" || f.parent === "dependent")
    .map((f) => f.name);
  const cascadeKey = cascadeFieldNames
    .map((name) => `${name}:${values[name] ?? ""}`)
    .join("|");

  const lastProcessedKeyRef = useRef(null);

  // ── Generic load for ALL parent/dependent cascades ────────────────────────
  useEffect(() => {
    // Skip if we've already processed this exact combination of cascade
    // values (avoids redundant re-fetching every render, and avoids
    // duplicating the fetch that handleFieldChangeInternal already does
    // when the user manually changes a field).
    if (cascadeKey === lastProcessedKeyRef.current) return;
    lastProcessedKeyRef.current = cascadeKey;

    const loadAllInitialDependents = async () => {
      // ── Level 1: fields with parent "main" that already have a value ──────
      // e.g. regionId, BankId
      const mainFields = fields.filter(
        (f) => f.parent === "main" && values[f.name]
      );

      for (const field of mainFields) {
        try {
          const options = await onFieldChange(field, values[field.name]);

          // Find the child field that depends on this main field
          const childField = fields.find((f) => f.dependentOn === field.name);
          if (childField && options) {
            // IMPORTANT: keyed by the child field's own name so that
            // independent cascades (e.g. regionId->branchId and
            // BankId->BankBranchId) never overwrite each other's options,
            // even though they share the same "dependent"/"subdependent" role.
            if (childField.parent === "dependent") {
              setDependentOptions((prev) => ({
                ...prev,
                [childField.name]: options || [],
              }));
            } else if (childField.parent === "subdependent") {
              setSubDependentOptions((prev) => ({
                ...prev,
                [childField.name]: options || [],
              }));
            }
          }
        } catch (error) {
          console.error(`Error loading dependents for ${field.name}:`, error);
        }
      }
     
      // ── Level 2: fields with parent "dependent" that already have a value ──
      // e.g. branchId (which itself depends on regionId). Without this pass,
      // a "subdependent" child like fieldOfficerId (which depends on
      // branchId, not on a "main" field) never gets its options loaded on
      // mount/edit — the user would have to manually reselect the branch to
      // trigger it. This mirrors level 1 but one cascade level down.
      const dependentFieldsWithValue = fields.filter(
        (f) => f.parent === "dependent" && values[f.name]
      );

      for (const field of dependentFieldsWithValue) {
        try {
          const subOptions = await onFieldChange(field, values[field.name]);

          const childField = fields.find((f) => f.dependentOn === field.name);
          if (childField && childField.parent === "subdependent" && subOptions) {
            setSubDependentOptions((prev) => ({
              ...prev,
              [childField.name]: subOptions || [],
            }));
          }
        } catch (error) {
          console.error(`Error loading subdependents for ${field.name}:`, error);
        }
      }
    };

    if (onFieldChange && fields.length > 0) {
      loadAllInitialDependents();
    }
    // Re-run whenever the relevant cascade values change (covers both the
    // initial mount and any later async reinitialization from the parent).
  }, [cascadeKey]);

  return null;
};

const DynamicForm = ({
  fields,
  onSubmit,
  title,
  subtitle,
  initialValues,
  onFieldChange,
}) => {
  // ── Keyed by the DEPENDENT field's own name, e.g. { branchId: [...], BankBranchId: [...] } ──
  // Using a shared array here (instead of a map keyed by field name) was the
  // root cause of two independent cascades (region->branch and
  // bank->bankBranch) stomping on each other's options.
  const [dependentOptions, setDependentOptions] = useState({});
  const [subdependentOptions, setSubDependentOptions] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [preview, setPreview] = useState({});
  const [fieldOptions, setFieldOptions] = useState({});
  const anchorRef = useRef(null);
  const [openPopper, setOpenPopper] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");

  const fieldsPerPage = 10;
  const totalPages = Math.ceil(fields.length / fieldsPerPage);
   const PLAIN_NUMBER_FIELD_KEYWORDS = ["idno", "passport", "idnoorpassport","paybill","contactPhoneNumber"];

      const isPlainNumberField = (field) => {
          const name = (field.name || "").toLowerCase();
          return PLAIN_NUMBER_FIELD_KEYWORDS.some((keyword) => name.includes(keyword));
        };

// while still playing nicely with Formik's onChange(name, value) pattern.
const NumberFormatInput = React.forwardRef(function NumberFormatInput(
  props,
  ref
) {
  const {
    onChange,
    name,
    thousandSeparator = true,
    decimalScale,
    allowNegative = false,
    prefix,
    suffix,
    ...other
  } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      name={name}
      thousandSeparator={thousandSeparator}
      decimalScale={decimalScale}
      allowNegative={allowNegative}
      prefix={prefix}
      suffix={suffix}
      valueIsNumericString
      onValueChange={(values) => {
        onChange({
          target: {
            name,
            value: values.floatValue ?? null,
          },
        });
      }}
    />
  );
});
  const validateDOB = (value) => {
    const today = new Date();
    const minAllowedDate = new Date();
    minAllowedDate.setFullYear(today.getFullYear() - 18);
    if (new Date(value) > minAllowedDate) {
      return "You must be at least 18 years old.";
    }
    return undefined;
  };

  const today = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(today.getFullYear() - 18);
  const maxDateString = maxDate.toISOString().split("T")[0];
  const maxBusinessDAte = today.toISOString().split("T")[0];

  const validationSchema = yup.object().shape(
    fields.reduce((acc, field) => {
      let schema;
      switch (field.type) {
        case "text":
          schema = yup.string().nullable();
          break;
        case "number":
          schema = yup.number().nullable();
          break;
        case "date":
          schema = yup.date().nullable();
          break;
        case "select":
          schema = yup.string().nullable();
          break;
        case "multiselect":
          schema = yup.array().nullable();
          break;
        case "file":
          // ── Files come through as base64 strings (or null/undefined when
          // nothing has been selected/loaded yet). Explicitly reject empty
          // string too, since some flows may seed the field with "" rather
          // than null — `.required()` alone only rejects null/undefined.
          schema = yup
            .string()
            .nullable()
            .test(
              "file-present",
              `${field.label} is required`,
              (value) => !field.required || (!!value && value.length > 0)
            );
          break;
        case "checkbox":
          schema = yup.boolean().nullable();
          break;
        default:
          schema = yup.string().nullable();
      }

      if (field.name === "username") {
        schema = yup.string().min(5).max(20).required("Username is required");
      }
      if (field.name === "idno") {
        schema = yup.string().matches(/^\d{7,}$/, "ID must be at least 7 digits").required();
      }
      if (
        field.name === "phone" ||
        field.name === "mobileNumber" ||
        field.name === "contactPhoneNumber" ||
        field.name === "phoneContact"
      ) {
        schema = yup
          .string()
          .matches(
            /^(?:(?:\+?254|0)[17]\d{8})$/,
            "Phone number must be in Kenyan format (e.g. 254123456789, 0712345678, or +254712345678)"
          )
          .required(`${field.label || "Phone number"} is required`);
      }
      if (field.name === "idNoOrPassport") {
        schema = yup.string().matches(/^[0-9]{6,13}$/, "ID/Passport number must be between 6 and 13 digits").required();
      }
      if (field.name === "email") {
        schema = yup.string().test("contains-at", "Email must contain '@'", (value) => value && value.includes("@")).email("Invalid email format");
      }
      // NOTE: file fields already build their own "required" behavior above
      // via the "file-present" test (yup.mixed()/.string().required() treats
      // an empty string as present, which would silently defeat this for
      // file inputs), so we skip the generic required() wrap for them here.
      if (field.required && field.type !== "file") {
        schema = schema.required(`${field.label} is required`);
      }
      acc[field.name] = schema;
      return acc;
    }, {})
  );

  const handleFieldChangeInternal = useCallback(async (
    setFieldValue,
    field,
    value,
    values
  ) => {
    setFieldValue(field.name, value);

    if (field.parent === "main" && onFieldChange) {
      const options = await onFieldChange(field, value);
      // Determine where to store based on the child field's parent type.
      // Keyed by childField.name so this cascade's options never collide
      // with a different cascade that also uses "dependent"/"subdependent".
      const childField = fields.find((f) => f.dependentOn === field.name);
      if (childField) {
        if (childField.parent === "dependent") {
          setDependentOptions((prev) => ({
            ...prev,
            [childField.name]: options || [],
          }));
        } else if (childField.parent === "subdependent") {
          setSubDependentOptions((prev) => ({
            ...prev,
            [childField.name]: options || [],
          }));
        }
      }
    }
    if (field.parent === "dependent" && onFieldChange) {
      const subOptions = await onFieldChange(field, value);
      // Only write into subdependentOptions if this "dependent" field
      // actually has a "subdependent" child (e.g. branchId -> fieldOfficerId).
      // Fields like BankBranchId are "dependent" but have no further child,
      // so without this guard they would wipe out fieldOfficerId's options
      // any time the bank branch was changed.
      const childField = fields.find((f) => f.dependentOn === field.name);
      if (childField && childField.parent === "subdependent") {
        setSubDependentOptions((prev) => ({
          ...prev,
          [childField.name]: subOptions || [],
        }));
      }
    }
  }, [fields, onFieldChange]);

  const handleDateRangeChange = (range, setFieldValue) => {
    const EndDate = dayjs().format("YYYY-MM-DD");
    let startDate;
    switch (range) {
      case "lastMonth":
        startDate = dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
        setFieldValue("asAt", startDate);
        break;
      case "last3Months":
        startDate = dayjs().subtract(3, "month").startOf("month").format("YYYY-MM-DD");
        setFieldValue("asAt", startDate);
        break;
      case "last6Months":
        startDate = dayjs().subtract(6, "month").startOf("month").format("YYYY-MM-DD");
        setFieldValue("asAt", startDate);
        break;
      default:
        startDate = "";
    }
    setFieldValue("startDate", startDate);
    setFieldValue("endDate", EndDate);
  };

  const handleDateFieldClick = (event) => {
    handleclickdate(event);
    setOpenPopper(true);
  };

  const handleClosePopper = () => {
    setOpenPopper(false);
  };

  const shouldDisableField = (field, values) => {
    if (field.readOnly) return true;
    if (field.disableCondition) {
      const disableField = fields.find(
        (f) => f.name === field.disableCondition.fieldName
      );
      return values[disableField.name] === field.disableCondition.value;
    }
    return false;
  };

  const handleclickdate = () => {
    console.log("clicked the date");
  };

  const getPaginatedFields = () => {
    const startIndex = currentPage * fieldsPerPage;
    return fields.slice(startIndex, startIndex + fieldsPerPage);
  };

  const resizeImage = (
    file,
    maxWidth = 800,
    maxHeight = 800,
    initialQuality = 0.9,
    maxSizeMB = 5
  ) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height *= maxWidth / width;
              width = maxWidth;
            } else {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          let quality = initialQuality;
          const tryCompress = () =>
            new Promise((resolveTry) => {
              canvas.toBlob(
                (blob) => { resolveTry(blob); },
                "image/jpeg",
                quality
              );
            });
          let blob = await tryCompress();
          while (blob.size / (1024 * 1024) > maxSizeMB && quality > 0.1) {
            quality -= 0.1;
            blob = await tryCompress();
          }
          if (blob.size / (1024 * 1024) <= maxSizeMB) {
            resolve(blob);
          } else {
            reject(new Error("Unable to reduce image below 5MB"));
          }
        };
        img.onerror = (e) => reject(e);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const convertFileToBase64 = (file) => {
    if (!file) return null;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result.split(",")[1]);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box m="20px">
        <Header title={title} subtitle={subtitle} />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            setFieldError,
            setFieldTouched,
            isSubmitting,
            validateForm,
          }) => (
            <form onSubmit={handleSubmit}>
              <FormikObserver
                onFieldChange={onFieldChange}
                fields={fields}
                setDependentOptions={setDependentOptions}
                setSubDependentOptions={setSubDependentOptions}
              />
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                }}
              >
                {getPaginatedFields().map((field) => {
                  const isDependent =
                    field.dependentOn && !values[field.dependentOn];
                  const isDisabled = shouldDisableField(field, values);

                  // Resolve this specific field's own options, keyed by its name,
                  // so it never sees another cascade's options.
                  const fieldOptionsForRole =
                    field.parent === "dependent"
                      ? dependentOptions[field.name]
                      : field.parent === "subdependent"
                        ? subdependentOptions[field.name]
                        : (fieldOptions[field.name] ?? field.options);

                  return (
                    <Box key={field.name} sx={{ gridColumn: field.gridColumn }}>
                      {field.type === "select" || field.type === "multiselect" ? (
                        Array.isArray(fieldOptionsForRole) ? (
                          <Autocomplete
                            fullWidth
                            disabled={isDependent || isDisabled}
                            multiple={field.type === "multiselect"}
                            options={fieldOptionsForRole || []}
                            value={
                              field.type === "multiselect"
                                ? (field.options || []).filter(
                                  (option) =>
                                    Array.isArray(values[field.name || ""]) &&
                                    values[field.name || ""]?.includes(option.value || "")
                                )
                                : (fieldOptionsForRole || []).find(
                                    (option) =>
                                      option.value === values[field.name]
                                  ) || null
                            }
                            getOptionLabel={(option) =>
                              option ? option.label : ""
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.value === (value?.value ?? value)
                            }
                            onInputChange={async (event, inputValue, reason) => {
                              if (reason !== "input") return;
                              if (inputValue && field.queryConfig) {
                                const {
                                  queryName,
                                  variables,
                                  transformResult,
                                  baseUrl,
                                  searchParamKey = "searchInput", // default preserves existing fields' behavior
                                  flatArgs = false,               // true => use fetchJsonDynamicFlatArgs (no input-object wrapper)
                                } = field.queryConfig;
                                try {
                                  const params = {
                                    ...variables,
                                    [searchParamKey]: inputValue || [],
                                  };
                                  const results = flatArgs
                                    ? await fetchJsonDynamicFlatArgs(queryName, params, baseUrl)
                                    : await fetchJsonDynamic(queryName, null, params, baseUrl);
                                    const parsedResults = results?.results
                                      ? JSON.parse(results.results)
                                      : [];
                                  const transformedOptions = transformResult
                                    ? transformResult(parsedResults || [])
                                    : parsedResults?.map((item) => ({
                                      value: item.id,
                                      label: item.name || item.label || item.mobileNumber,
                                    }));
                                  setFieldOptions((prevOptions) => ({
                                    ...prevOptions,
                                    [field.name]: transformedOptions || [],
                                  }));
                                } catch (error) {
                                  console.error(`Error fetching options for ${field.name}:`, error);
                                  setFieldOptions((prevOptions) => ({
                                    ...prevOptions,
                                    [field.name]: [],
                                  }));
                                }
                              }
                            }}
                            onChange={(event, newValue) => {
                              if (field.skipInternalOnChange && field.onChange) {
                                if (newValue === null) {
                                  setFieldValue(field.name, null);
                                  return;
                                }
                                field.onChange(event, newValue?.value ?? null);
                                setFieldValue(
                                  field.name,
                                  field.type === "multiselect"
                                    ? newValue.map((item) => item.value)
                                    : newValue ? newValue.value : ""
                                );
                              } else {
                                handleFieldChangeInternal(
                                  setFieldValue,
                                  field,
                                  field.type === "multiselect"
                                    ? newValue.map((item) => item.value)
                                    : newValue ? newValue.value : "",
                                  values
                                );
                              }
                            }}
                            onBlur={handleBlur}
                            renderInput={(params) => (
                              <TextField
                                fullWidth
                                {...params}
                                label={field.label}
                                variant="filled"
                                name={field.name}
                                error={!!touched[field.name] && !!errors[field.name]}
                                helperText={touched[field.name] && errors[field.name]}
                              />
                            )}
                          />
                        ) : (
                          <div className="loading-container">
                            <span className="loading-spinner"></span> Loading options...
                          </div>
                        )
                      ) : field.type === "checkbox" ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!values[field.name]}
                              onChange={(event) => {
                                const newValue = event.target.checked;
                                setFieldValue(field.name, newValue);
                                // Always notify parent for checkbox changes
                                if (onFieldChange) {
                                  onFieldChange(field, newValue);
                                }
                              }}
                              name={field.name}
                              disabled={isDisabled}
                            />
                          }
                          label={field.label}
                        />
                      ) : field.type === "file" ? (
                        <Box>
                          <Button
                            variant="contained"
                            component="label"
                            disabled={isDisabled}
                            // Visually flag a required-but-empty file field the
                            // same way other inputs turn red via `error`.
                            color={
                              touched[field.name] && errors[field.name]
                                ? "error"
                                : "primary"
                            }
                          >
                            {field.label}
                            {field.required ? " *" : ""}
                            <input
                              type="file"
                              hidden
                              accept={field.accept || "/*"}
                              onChange={async (event) => {
                                const file = event.target.files[0];
                                if (file) {
                                  try {
                                    const resizedFile = await resizeImage(file);
                                    const base64String = await convertFileToBase64(resizedFile);
                                    setFieldValue(field.name, base64String);
                                    // Mark touched immediately so the required
                                    // error (or its absence) is visible right
                                    // away, instead of only after a failed
                                    // submit attempt.
                                    setFieldTouched(field.name, true, true);
                                    const objectURL = URL.createObjectURL(file);
                                    setPreview((prev) => ({
                                      ...prev,
                                      [field.name]: objectURL,
                                    }));
                                    return () => URL.revokeObjectURL(objectURL);
                                  } catch (error) {
                                    console.error(`Error processing file for ${field.name}:`, error);
                                  }
                                }
                              }}
                            />
                          </Button>
                          {touched[field.name] && errors[field.name] ? (
                            <Box
                              sx={{
                                color: "error.main",
                                fontSize: "0.75rem",
                                mt: "3px",
                                ml: "14px",
                              }}
                            >
                              {errors[field.name]}
                            </Box>
                          ) : null}
                          {preview[field.name] || initialValues[field.name] ? (
                            <Box mt={2}>
                              <img
                                src={
                                  preview[field.name]
                                    ? preview[field.name]
                                    : initialValues[field.name] instanceof File
                                      ? URL.createObjectURL(initialValues[field.name])
                                      : initialValues[field.name]?.startsWith("data:image/")
                                        ? initialValues[field.name]
                                        : `data:image/png;base64,${values[field.name]}`
                                }
                                alt={field.label}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "150px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  padding: "5px",
                                }}
                              />
                            </Box>
                          ) : null}
                        </Box>
                      ) : field.type === "date" ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          width="100%"
                          position="relative"
                          ref={anchorRef}
                        >
                          <DatePicker
                            label={field.label}
                            value={values[field.name] ? dayjs(values[field.name]) : null}
                            onChange={(newValue) => {
                              const formattedDate = newValue ? newValue.format('YYYY-MM-DD') : null;
                              setFieldValue(field.name, formattedDate);
                            }}
                            maxDate={
                              field.name === "dob" || field.name === "dateOfBirth"
                                ? dayjs(maxDateString)
                                : field.name === "yearOfEstablishment" || field.name === "yearofpurchase"
                                  ? dayjs(maxBusinessDAte)
                                  : undefined
                            }
                            slotProps={{
                              textField: {
                                variant: 'filled',
                                fullWidth: true,
                                error: !!touched[field.name] && !!errors[field.name],
                                helperText: touched[field.name] && errors[field.name],
                                onBlur: handleBlur
                              },
                              actionBar: {
                                actions: ['clear', 'accept'],
                              },
                            }}
                            disabled={isDisabled || field.readonly}
                            sx={{
                              '& .MuiInputBase-root': {
                                height: '56px',
                              },
                            }}
                          />
                          {field.showCustomDate && (
                            <Popper
                              open={openPopper}
                              anchorEl={anchorRef.current}
                              placement={isMobile ? "bottom-end" : "right-start"}
                              modifiers={[
                                {
                                  name: "offset",
                                  options: { offset: [10, 0] },
                                },
                              ]}
                            >
                              <Box
                                sx={{
                                  p: 1,
                                  bgcolor: "background.paper",
                                  boxShadow: 3,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                  mt: isMobile ? 15.7 : 5.5,
                                  borderRadius: 2,
                                  width: { xs: "100%", sm: "auto" },
                                }}
                                tabIndex={0}
                                onBlur={(event) => {
                                  if (!event.currentTarget.contains(event.relatedTarget)) {
                                    handleClosePopper();
                                  }
                                }}
                              >
                                {[
                                  { label: "Custom Date", value: "" },
                                  { label: "Last Month", value: "lastMonth" },
                                  { label: "Last 3 Months", value: "last3Months" },
                                  { label: "Last 6 Months", value: "last6Months" },
                                  { label: "Last One Year", value: "lastOneYear" },
                                ].map((option) => (
                                  <Button
                                    key={option.value}
                                    variant="text"
                                    fullWidth
                                    onClick={() => {
                                      handleDateRangeChange(option.value, setFieldValue);
                                      handleClosePopper();
                                    }}
                                    sx={{ borderRadius: 2 }}
                                  >
                                    {option.label}
                                  </Button>
                                ))}
                              </Box>
                            </Popper>
                          )}
                        </Box>
                      ) : (
                        <TextField
                          disabled={isDisabled || field.readonly}
                          fullWidth
                          variant="filled"
                          type={
                            field.type === "number"
                              ? isPlainNumberField(field)
                                ? "text" // or "number" if you want the native numeric keypad on mobile
                                : "text" // still "text" here because NumberFormatInput needs a text input
                              : field.type
                          }
                          label={field.label}
                          onBlur={(e) => {
                            handleBlur(e);
                            if (field.onBlur) {
                              field.onBlur(e, setFieldValue, values, setFieldError);
                            }
                          }}
                          onChange={handleChange}
                          value={values[field.name] ?? ""}
                          name={field.name}
                          error={!!touched[field.name] && !!errors[field.name]}
                          helperText={touched[field.name] && errors[field.name]}
                          InputProps={
                            field.type === "number" && !isPlainNumberField(field)
                              ? {
                                  inputComponent: NumberFormatInput,
                                  inputProps: {
                                    thousandSeparator: field.thousandSeparator ?? true,
                                    decimalScale: field.decimalScale ?? 2,
                                    allowNegative: field.allowNegative ?? false,
                                    prefix: field.prefix,
                                    suffix: field.suffix,
                                  },
                                }
                              : undefined
                          }
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
              <Box display="flex" justifyContent="space-between" mt="20px">
                <Button
                  type="button"
                  color="primary"
                  variant="contained"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  Previous
                </Button>
                {currentPage < totalPages - 1 ? (
                  <Button
                    type="button"
                    color="secondary"
                    variant="contained"
                    onClick={async (e) => {
                      e.preventDefault();
                      const currentFields = getPaginatedFields().map(
                        (field) => field.name
                      );
                      const validationErrors = await validateForm();
                      const hasErrors = currentFields.some(
                        (fieldName) => validationErrors[fieldName]
                      );
                      if (!hasErrors) {
                        setCurrentPage((prev) => prev + 1);
                      } else {
                        // Mark every field with an error on this page as
                        // touched so its error message (file fields included)
                        // actually renders instead of failing silently.
                        currentFields.forEach((fieldName) => {
                          if (validationErrors[fieldName]) {
                            setFieldTouched(fieldName, true, false);
                          }
                        });
                        const firstErrorField = currentFields.find(
                          (fieldName) => validationErrors[fieldName]
                        );
                        if (firstErrorField) {
                          const errorElement = document.querySelector(
                            `[name="${firstErrorField}"]`
                          );
                          errorElement?.focus();
                        }
                      }
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    Submit
                  </Button>
                )}
              </Box>
              {isSubmitting && <CircularProgress />}
            </form>
          )}
        </Formik>
      </Box>
    </LocalizationProvider>
  );
};

export default DynamicForm;